import axios, { capi, eplApi, extractData, extractError, honorariumAdvisorApi, searchEmployees } from '@atheneum/api'
import { renderExperience } from '@atheneum/api/dist/lib'
import assign from 'lodash/assign'
import get from 'lodash/get'
import { action, computed, extendObservable, toJS } from 'mobx'


const EUR_CURRENCY_ID = 1

class EPLModel {
  constructor(epl, staticData, growlStore, eplStore, capiProject, capiCall) {
    extendObservable(this, {
      epl: { ...epl },
      id: epl.id,
      capiProject,
      capiCall,
      details: null,
      staticData,
      growlStore,
      component: null,
      batchChecked: false,
      projectedMargin: null,
      plutusPrediction: null,
      recommendedHonorarium: null,
      recommendedHonorariumCurrencyId: null,
      isVisible: false,
      height: 187,
      expanded: false,
      collapse: false,
      expandComplete: false,
      eplValuesBeingUpdated: false,
      loading: false,
      working: false,
      eplStore: eplStore,
      status: computed(() => {
        const names = staticData.names
        const name = names.eplStatuses[this.eplStatusId]
        return name
      }),
      relevantExperience: computed(() => {
        return get(this, 'epl.relevantExperience.experience', null)
      }),
      position: computed(() => {
        return get(this.relevantExperience, 'position.name', '')
      }),
      formattedExperience: computed(() => {
        return renderExperience(this.relevantExperience)
      }),

      error: null
    })
  }

  expand = action(() => {
    if (!this.expanded) this.loading = true
    if (this.expanded) {
      this.collapse = true
      this.expandComplete = false
      this.height = 187

      return setTimeout(() => {
        this.expanded = false
        this.collapse = false
      }, 500)
    }

    return eplApi
      .get(this.epl.id)
      .then(async epl => {
        this.details = epl
        await this.getHonorariumAdvice(epl)
        this.projectedMargin = epl.projectedMargin
        this.expanded = true
        this.loading = false
        this.expandComplete = false

        setTimeout(() => {
          this.expandComplete = true
          this.height = this.component
            ? this.component.clientHeight
            : this.height
        }, 500)
      })
      .catch(err => {
        this.error = err
        this.loading = false
      })
  })

  getHonorariumAdvice(epl) {
    const honorariumData = {
      data: {
        ML_EX_region_geo: epl.expert.address.country.expertFocusRegionId,
        ML_EX_country: epl.expert.address.countryId,
        UUID_EX: epl.expertId,
        UUID_PR: epl.projectId,
        UUID_OFFICE: epl.atheneumOfficeId,
        ML_PR_industry: epl.project.industryId,
        ML_PR_type: epl.project.projectTypeId,
        ML_OLD_PR_sub_industry: epl.project.subIndustryId
      },
      value_base: 25,
      model_filename: "m2"
    }

    return honorariumAdvisorApi.getHonorariumAdvice(honorariumData)
      .then(async (result) => {
        this.plutusPrediction = result.honorarium
        await this.calculateHonorarium(epl)
      })
      .catch(err => {
        console.error(err)
      })
  }

  calculateHonorarium = async (epl) => {
    const { honorariumCurrencyId } = epl
    const amount = this.plutusPrediction
    if (!amount) {
      return
    }

    if (honorariumCurrencyId && honorariumCurrencyId !== EUR_CURRENCY_ID) {
      const converted = await eplApi.convertCurrency({
        amount,
        fromCurrencyId: EUR_CURRENCY_ID,
        toCurrencyId: epl.honorariumCurrencyId
      })
      this.recommendedHonorarium = converted
      this.recommendedHonorariumCurrencyId = honorariumCurrencyId
      return
    }

    this.recommendedHonorarium = amount
    this.recommendedHonorariumCurrencyId = EUR_CURRENCY_ID
  }

  batchCheck = action(value => {
    this.batchChecked = value || !this.batchChecked
  })

  handleCancellation = action(({ date, eplStatusComment }) => {
    this.epl.interviewCanceledDate = date
    this.epl.eplStatusCommentId = eplStatusComment.id
    this.epl.eplStatusId = this.staticData.CONST.EPL_STATUSES.NOT_INTERVIEWED
    return this.update({ updateDetails: false, updateFilter: true })
  })

  handleInterviewed = action(interviewDate => {
    this.epl.interviewDate = interviewDate
    this.epl.eplStatusId = this.staticData.CONST.EPL_STATUSES.INTERVIEWED
    return this.update({
      updateDetails: false,
      updateFilter: true,
      disableNotification: true
    })
  })

  getScheduleDate = () => {
    const availabilities = this.epl.expertAvailabilityToEpls
    const availabilityWithSchedule = availabilities.find(
      availability =>
        availability.schedule && availability.schedule.canceled === false
    )

    return availabilityWithSchedule && availabilityWithSchedule.schedule
      ? availabilityWithSchedule.schedule.startTime
      : null
  }

  typeAhead(input) {
    return searchEmployees({ q: input, employeeStatus: 'active' })
  }

  handleAutocomplete = input => {
    if (!input) return Promise.resolve([])

    return this.typeAhead(input)
      .then(res => {
        const extractedData = extractData(res)
        const options = extractedData.rows
        return options
      })
      .catch(extractError)
  }

  handleUpdateOnChange = ({ fields, updateDetails, updateFilter }) => {
    this.epl = assign(this.epl, fields)
    const update = updateFilter !== false
    this.update({ updateDetails, updateFilter: update })
  }


  changeReplyStatus = ({ replyStatusId, updateFilter }) => {
    this.loading = true
    return axios
      .put('/api/expert-project-link/' + this.epl.id + '/reply-status', {
        replyStatusId
      })
      .then(res => {
        this.error = null
        this.loading = false
        this.growlStore.showSuccess('EPL reply status updated.')

        if (updateFilter) this.eplStore.getFilterOptions(this.epl.projectId)
        this.get()
      })
      .catch(err => {
        this.error = err
        this.loading = false
        this.growlStore.showError(this.error)
      })
  }

  get = async () => {
    this.loading = true
    const epl = await eplApi.get(this.epl.id)
    if (this.capiProject) {
      try {
        this.capiCall = await capi.getCallByEplId(this.epl.id)
      } catch (e) {
        console.error(e)
      }
    }

    //epl === details, should get rid of one of them
    this.epl = epl
    this.details = epl
    this.projectedMargin = epl.projectedMargin
    this.eplValuesBeingUpdated = false
    this.loading = false
  }

  getDetails = action(() => {
    this.loading = true
    return eplApi
      .get(this.epl.id)
      .then(details => {
        this.details = details
        this.loading = false
      })
      .catch(err => {
        this.error = err
        this.loading = false
      })
  })

  formatCurrency = raw => Number(raw.replace(/\./g, '').replace(/,/g, '.'))

  transformData = data => {
    const experience = get(this.epl, 'relevantExperience.experience', {})

    const { position, company } = experience
    const transformedData = Object.assign({}, toJS(data))

    transformedData.relevantExperience.experience.position =
      position && position.name
    transformedData.relevantExperience.experience.company =
      company && company.name

    return transformedData
  }

  execute = action(fn => {
    this.working = true
    return fn()
      .then((r) => {
        this.working = false
        return r
      })
      .catch((e) => {
        this.working = false
        throw e
      })
  })

  update = action(({ updateDetails, disableNotification, updateFilter }) => {
    this.loading = true
    const eplData = this.transformData(this.epl)
    return axios
      .put('/api/expert-project-link/' + this.epl.id, eplData)
      .then(res => {
        this.error = null
        this.loading = false

        if (!disableNotification) {
          this.growlStore.showSuccess('EPL successfully updated.')
        }

        return Promise.all([
          updateDetails ? this.getDetails() : this.get(),
          updateFilter
            ? this.eplStore.getFilterOptions(this.epl.projectId)
            : Promise.resolve()
        ])
      })
      .catch(err => {
        this.error = extractError(err)
        this.loading = false
        updateDetails ? this.getDetails() : this.get()

        if (!disableNotification) {
          this.growlStore.showError(this.error)
        } else {
          throw extractError(err)
        }
      })
  })

  sendToPlutus = async (amount, currencyId, epl) => {
    let associate_pred = amount

    if (currencyId && currencyId !== EUR_CURRENCY_ID) { // Plutus operates in EUR only
      associate_pred = await eplApi.convertCurrency({
        amount,
        fromCurrencyId: currencyId,
        toCurrencyId: EUR_CURRENCY_ID,
      })
    }

    const eplHonorariumData = {
      UUID_EX: epl.expertId,
      ML_EX_country: epl.expert.address.countryId,
      ML_EX_region_geo: epl.expert.address.country.expertFocusRegionId,
      UUID_OFFICE: epl.atheneumOfficeId,
      UUID_PR: epl.projectId,
      ML_PR_industry: epl.project.industryId,
      ML_PR_type: epl.project.projectTypeId,
      ML_OLD_PR_sub_industry: epl.project.subIndustryId,
      associate_pred,
      plutus_pred: this.plutusPrediction,
    }

    honorariumAdvisorApi.logHonorariumToPlutus(eplHonorariumData)
  }

  autosave = async (eplId, payload) => {
    await this.calculateHonorarium(payload)

    if (
      payload.honorarium && payload.honorariumCurrencyId
      && (payload.honorarium !== this.details.honorarium
        || payload.honorariumCurrencyId !== this.details.honorariumCurrencyId)) {
      this.sendToPlutus(payload.honorarium, payload.honorariumCurrencyId, this.details)
    }

    return eplApi
      .autosave(eplId, payload)
      .then(() => {
        this.error = null
        this.loading = false
        this.growlStore.showSuccess('EPL successfully updated.')
      })
      .catch(err => {
        this.error = extractError(err)
        this.loading = false
        this.growlStore.showError(this.error)
      })
      .then(() => {
        this.get()
      })
  }

  changeDeliveredBy = (eplId, userId) => {
    this.loading = true
    return eplApi
      .updateDeliveredBy(eplId, { deliveredBy: userId })
      .then(({ teamInfo }) => {
        this.error = null
        this.loading = false
        this.growlStore.showSuccess('EPL successfully updated.')
        this.details.teamInfo = teamInfo
      })
      .catch(err => {
        this.error = err
        this.loading = false
        this.growlStore.showError(this.error)
      })
      .then(() => this.get())
  }

  changeNameVisibility({ epl, hide }) {
    this.loading = true
    return eplApi
      .setNameVisibility(epl.id, { hide: hide })
      .then(res => {
        this.error = null
        this.loading = false
        this.growlStore.showSuccess(res.message)
      })
      .catch(err => {
        this.error = err
        this.loading = false
        this.growlStore.showError(this.error)
      })
      .then(() => this.get())
  }
}

export default EPLModel
